import { useState } from 'react'

import { applicationToQuoteCards } from '@/components/quotes/applicationToQuoteCards'
import { QuoteCard } from '@/components/quotes/QuoteCard'
import { QuoteCardSkeleton } from '@/components/quotes/QuoteCardSkeleton'
import type { QuoteCardData } from '@/components/quotes/quoteCardTypes'
import { UploadCarrierDocumentDialog } from '@/components/quotes/UploadCarrierDocumentDialog'
import { CopyButton } from '@/components/ui/CopyButton'
import { DocumentType } from '@/models/document'
import { CoverageType } from '@/models/insuranceCoverage'
import { Demo, DsSection } from '../primitives'
import {
  DELLWOOD_DECLINED_AND_WAITING,
  DELLWOOD_LINES,
  DELLWOOD_PARTLY_DECLINED,
  QUOTES,
} from '../prototypes/QuoteCard/quoteCardData'

const byId = (id: string) => QUOTES.find((quote) => quote.id === id)

// Both sides run the live mapper, so the pair is genuinely what the merged card replaced.
const groupedOffer = applicationToQuoteCards(DELLWOOD_LINES)
const ungroupedOffer = DELLWOOD_LINES.flatMap((line) => applicationToQuoteCards([line]))

// A carrier that answered on one line and not the other is still one offer, so the lines it
// didn't price show as rows on the same card rather than cards of their own.
const partlyDeclinedOffer = applicationToQuoteCards(DELLWOOD_PARTLY_DECLINED)
const declinedAndWaitingOffer = applicationToQuoteCards(DELLWOOD_DECLINED_AND_WAITING)

// What these two Dellwood indications actually hold in production: a premium the
// portal rated, and no terms on the row it was read off.
const unstatedTermsOffer = applicationToQuoteCards(
  DELLWOOD_LINES.map((line) => ({
    ...line,
    coverages: line.coverages.map((coverage) => ({ ...coverage, limit: null, deductible: null })),
  }))
)

const priced = byId('corgi-itemized')
// The same offer with the carrier's flag off — what every card renders as by default.
const bundled = priced && { ...priced, itemizedCheckoutStyle: undefined }
const recommended = byId('geico')
// The Geico quote behind PR #1644, verbatim: physical damage is written as
// comprehensive $500 against collision $1,000, which one deductible field could
// only ever report as neither.
const splitDeductible = recommended && {
  ...recommended,
  coverages: [
    { type: CoverageType.AUTO_LIABILITY, premium: 52_162, limit: 1_000_000, deductible: null },
    {
      type: CoverageType.AUTO_PHYSICAL_DAMAGE,
      premium: 1_028,
      limit: null,
      deductible: 1_000,
      comprehensiveDeductible: 500,
    },
    { type: CoverageType.MOTOR_TRUCK_CARGO, premium: 479, limit: 100_000, deductible: 2_500 },
    { type: CoverageType.GENERAL_LIABILITY, premium: 428, limit: 1_000_000, deductible: null },
  ],
}
const currentInsurer = byId('canal')
const applied = byId('nirvana')
const preparing = byId('biberk-preparing')
const errored = byId('progressive-error')
const explained = errored && {
  ...errored,
  failureReason: "This insurer couldn't find your DOT number. We're checking it with them.",
}
const needsDocuments = byId('corgi-needs-loss-run')
const sourcedThrough = byId('progressive-via-rocklake')
const customerUploaded = byId('customer-uploaded')
// The longest carrier name in production, on the one card that carries a delete button.
const longNameUploaded = customerUploaded && {
  ...customerUploaded,
  carrierName: 'Mitsui Sumitomo Insurance Company of America',
}

const noop = () => undefined

/** Renders the card plus the exact prop that produces the state shown. */
function StateDemo({ prop, children }: { prop: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="w-full max-w-md">{children}</div>
      <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
        <span>{prop}</span>
        <CopyButton
          value={prop}
          className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
        />
      </div>
    </div>
  )
}

/** Wired to the real dialog so the page shows what Upload opens, not just the banner. */
function NeedsDocumentsDemo({ quote }: { quote: QuoteCardData }) {
  const [target, setTarget] = useState<{ id: string; documentType: DocumentType } | null>(null)
  return (
    <>
      <QuoteCard
        quote={quote}
        hideCheckout
        onUploadDocument={(id, documentType) => setTarget({ id, documentType })}
      />
      <UploadCarrierDocumentDialog
        applicationId="design-system"
        indicationId={target?.id ?? null}
        documentType={target?.documentType ?? DocumentType.LOSS_RUN}
        carrierName={quote.carrierName}
        onClose={() => setTarget(null)}
      />
    </>
  )
}

export function QuoteCardSection() {
  return (
    <DsSection id="quote-card" title="Quote card">
      <Demo
        label="Summary vs focused — onCardClick collapses to a tap target, isFocused shows every detail"
        className="flex flex-wrap items-start gap-8"
      >
        {priced && (
          <StateDemo prop="onCardClick">
            <QuoteCard quote={priced} onCardClick={noop} />
          </StateDemo>
        )}
        {priced && (
          <StateDemo prop="isFocused">
            <QuoteCard quote={priced} isFocused />
          </StateDemo>
        )}
      </Demo>
      <Demo label="hideCheckout — drops every add-to-cart affordance">
        {priced && (
          <StateDemo prop="hideCheckout">
            <QuoteCard quote={priced} isFocused hideCheckout />
          </StateDemo>
        )}
      </Demo>
      <Demo label="Labels come from the quote data, not props" className="flex flex-wrap items-start gap-8">
        {recommended && (
          <StateDemo prop="quote.brokerRecommended">
            <QuoteCard quote={recommended} isFocused hideCheckout />
          </StateDemo>
        )}
        {currentInsurer && (
          <StateDemo prop="quote.isCurrentInsurer">
            <QuoteCard quote={currentInsurer} isFocused hideCheckout />
          </StateDemo>
        )}
        {sourcedThrough && (
          <StateDemo prop="quote.sourcedThrough">
            <QuoteCard quote={sourcedThrough} isFocused hideCheckout />
          </StateDemo>
        )}
      </Demo>
      <Demo
        label="Applied — the customer chose this quote at checkout. Marked by the confirmation banner under the price, which drops off once the carrier answers with a firm quote, and by a disabled “Checkout requested” in place of the cart CTA; no badge, no border"
        className="flex flex-wrap items-start gap-8"
      >
        {applied && (
          <StateDemo prop="quote.appliedAt">
            <QuoteCard quote={applied} onCardClick={noop} />
          </StateDemo>
        )}
        {applied && (
          <StateDemo prop="quote.appliedAt">
            <QuoteCard quote={applied} isFocused hideCheckout />
          </StateDemo>
        )}
      </Demo>
      <Demo
        label="One carrier, several separately-rated lines — Rocklake prices each line of business on its own portal, so a carrier answering on two arrives as two indications. Grouped, they read as the one offer they are"
        className="flex flex-wrap items-start gap-8"
      >
        <div id="quote-card-ungrouped" className="flex flex-wrap items-start gap-8">
          {ungroupedOffer.map((card) => (
            <StateDemo key={card.id} prop="one card per indication">
              <QuoteCard quote={card} isFocused hideCheckout />
            </StateDemo>
          ))}
        </div>
        <div id="quote-card-grouped" className="flex flex-wrap items-start gap-8">
          {groupedOffer.map((card) => (
            <StateDemo key={card.id} prop="quote.memberIds">
              <QuoteCard quote={card} isFocused hideCheckout />
            </StateDemo>
          ))}
        </div>
      </Demo>
      <Demo
        label="Lines that didn't price, beside one that did — a carrier that declined or is still working on one line keeps that line on the same card as a status row, rather than splitting into a second card. A card with no price at all carries no breakdown: its status is the whole answer"
        className="flex flex-wrap items-start gap-8"
      >
        <div id="quote-card-partly-declined" className="flex flex-wrap items-start gap-8">
          {partlyDeclinedOffer.map((card) => (
            <StateDemo key={card.id} prop="quote.coverageOutcomes">
              <QuoteCard quote={card} isFocused hideCheckout />
            </StateDemo>
          ))}
        </div>
        <div id="quote-card-declined-and-waiting" className="flex flex-wrap items-start gap-8">
          {declinedAndWaitingOffer.map((card) => (
            <StateDemo key={card.id} prop="quote.stage (nothing priced)">
              <QuoteCard quote={card} hideCheckout />
            </StateDemo>
          ))}
        </div>
      </Demo>
      <Demo
        label="Terms the carrier never stated — an em dash, not $0. Rocklake rates each line on its own portal and some rows print a premium with no limit or deductible; printing those as $0 reads as a real zero-dollar term"
        className="flex flex-wrap items-start gap-8"
      >
        <div id="quote-card-unstated-terms" className="flex flex-wrap items-start gap-8">
          {unstatedTermsOffer.map((card) => (
            <StateDemo key={card.id} prop="coverage.limit === null">
              <QuoteCard quote={card} isFocused hideCheckout />
            </StateDemo>
          ))}
        </div>
      </Demo>
      <Demo
        label="Physical damage written comprehensive + collision — two deductibles, named. Geico prices comprehensive and collision separately, so a row printing one of them misstates the other and a blank row tells the customer nothing"
        className="flex flex-wrap items-start gap-8"
      >
        {splitDeductible && (
          <div id="quote-card-split-deductible" className="flex flex-wrap items-start gap-8">
            <StateDemo prop="coverage.comprehensiveDeductible">
              <QuoteCard quote={splitDeductible} isFocused hideCheckout />
            </StateDemo>
          </div>
        )}
      </Demo>
      <Demo
        label="Bundled vs itemized checkout — a carrier flagged as pricing each coverage separately gives every coverage its own Add to cart. Unflagged (the default) the coverages are a bundle: one CTA, and the customer takes all of them to get that price"
        className="flex flex-wrap items-start gap-8"
      >
        {bundled && (
          <div id="quote-card-bundled">
            <StateDemo prop="itemizedCheckoutStyle === undefined">
              <QuoteCard quote={bundled} isFocused onAddCoverages={noop} />
            </StateDemo>
          </div>
        )}
        {priced && (
          <div id="quote-card-itemized">
            <StateDemo prop="ItemizedCheckoutStyle.CAN_PURCHASE_INDIVIDUAL">
              <QuoteCard quote={priced} isFocused onAddCoverages={noop} />
            </StateDemo>
          </div>
        )}
      </Demo>
      <Demo
        label="Carrier notes — the one carrier-details row that's dropped rather than blanked to No information. Only a couple of carriers publish a note, so the row would sit empty on nearly every card"
        className="flex flex-wrap items-start gap-8"
      >
        {recommended && (
          <div id="quote-card-with-carrier-note">
            <StateDemo prop="quote.carrierPublicNote">
              <QuoteCard quote={recommended} isFocused hideCheckout />
            </StateDemo>
          </div>
        )}
        {priced && (
          <div id="quote-card-without-carrier-note">
            <StateDemo prop="carrierPublicNote === undefined">
              <QuoteCard quote={priced} isFocused hideCheckout />
            </StateDemo>
          </div>
        )}
      </Demo>
      <Demo
        label="Customer-uploaded — the only card with a delete button. It's pinned to the card rather than laid out in the header, so the header reserves the room a long carrier name would otherwise truncate over"
        className="flex flex-wrap items-start gap-8"
      >
        {customerUploaded && (
          <StateDemo prop="onDelete">
            <QuoteCard quote={customerUploaded} isFocused hideCheckout onDelete={noop} />
          </StateDemo>
        )}
        {longNameUploaded && (
          <div id="quote-card-uploaded-long-name">
            <StateDemo prop="onDelete + long carrierName">
              <QuoteCard quote={longNameUploaded} isFocused hideCheckout onDelete={noop} />
            </StateDemo>
          </div>
        )}
      </Demo>
      <Demo
        label="Minimal quote — no premium priced yet. Every minimal state holds one height, so its skeleton crossfades without resizing"
        className="flex flex-wrap items-start gap-8"
      >
        {preparing && (
          <StateDemo prop="MinimalQuoteCard">
            <QuoteCard quote={preparing} hideCheckout />
          </StateDemo>
        )}
        {needsDocuments && (
          <div id="quote-card-needs-loss-run">
            <StateDemo prop="outstandingDocuments">
              <NeedsDocumentsDemo quote={needsDocuments} />
            </StateDemo>
          </div>
        )}
        <StateDemo prop="QuoteCardSkeleton">
          <QuoteCardSkeleton />
        </StateDemo>
      </Demo>
      <Demo label="Why a carrier didn't price — once the failure analysis has explained a failed submission, the card says why in plain words instead of the generic failure">
        <div id="quote-card-failure-reason" className="flex flex-wrap items-start gap-8">
          {errored && (
            <StateDemo prop="failureReason === null">
              <QuoteCard quote={errored} hideCheckout />
            </StateDemo>
          )}
          {explained && (
            <StateDemo prop="quote.failureReason">
              <QuoteCard quote={explained} hideCheckout />
            </StateDemo>
          )}
        </div>
      </Demo>
    </DsSection>
  )
}
